import { rekeyClaimAction, resetRekey } from 'feature/claims/claimSlice';
import { getMemberDetails, resetMember } from 'feature/members/memberSlice';
import { FC, Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';

import { ReactComponent as CloseDialog } from 'assett/icons/close_dialog_icon.svg';
import lodash from 'lodash';
import { IconButton } from 'styles';
import Button from 'styles/button/Button';
import Dialog from 'styles/dialog/SimpleDialog';
import { IClaim } from 'types/Claim';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import PriceEstimate from './PriceEstimate';

const DialogRekey: FC<{
	claim: IClaim;
	open: boolean;
	setOpen: (value: boolean) => void;
}> = ({ claim, open, setOpen }) => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const dispatch = useDispatch<AppDispatch>();

	const { rekeyClaim, statusCalculation } = useSelector(
		(state: RootState) => state.claim,
	);
	const [formData, setFormData] = useState<IClaim>(lodash.cloneDeep(claim));

	useEffect(() => {
		setFormData(lodash.cloneDeep(rekeyClaim || claim)); //initially the original claim is loaded. then when calculation is done the rekey value is saved and calculated values should be shown
	}, [claim, rekeyClaim]);

	// every time the dialog opens or closes the rekey state and the member benefits are reset;
	// on open the benefits (max amounts, other items) are loaded again
	useEffect(() => {
		dispatch(resetRekey());
		dispatch(resetMember());
		if (open) {
			dispatch(
				getMemberDetails({
					subscriptionUuid: claim.beneficiarySubscriptionUuid,
					dateOfService: claim.claimDate.split('T')[0],
				}),
			);
		}
	}, [claim.beneficiarySubscriptionUuid, claim.claimDate, dispatch, open]);

	const handleRekey = async () => {
		const result = await dispatch(
			rekeyClaimAction({
				uuid: claim.claimUUID!,
				formData: rekeyClaim as IClaim,
			}),
		);
		if (rekeyClaimAction.fulfilled.match(result)) {
			setOpen(false);
		}
	};

	return (
		<Dialog
			open={open}
			close={
				<IconButton onClick={() => setOpen(false)}>
					<CloseDialog />
				</IconButton>
			}
			title={tClientLabels('rekey.title')}
			events={true}
			actions={
				<Fragment>
					<Button variant="outlined" onClick={() => setOpen(false)}>
						{tClientLabels('commons.buttons.cancel')}
					</Button>
					<Button
						variant="contained"
						onClick={handleRekey}
						disabled={statusCalculation !== 'afterCalculation'}
					>
						{tClientLabels('commons.buttons.confirm')}
					</Button>
				</Fragment>
			}
			fullWidth={false}
			maxWidth={false}
		>
			<PriceEstimate formData={formData} showGross={false} />
		</Dialog>
	);
};

export default DialogRekey;
